import { BasemapProviders, wmsTileUrl } from './data-sources'

const uid = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

export const LayerTypes = Object.freeze({
  VECTOR: 'vector', RASTER: 'raster', TILE: 'tile', WMS: 'wms', WMTS: 'wmts', XYZ: 'xyz', DATABASE: 'database'
})

export const VECTOR_STYLE = {
  point: { color: '#58a6ff', size: 6, opacity: 0.95, outlineColor: '#e6edf3', outlineWidth: 1 },
  line: { color: '#58a6ff', width: 2, opacity: 0.9, dash: [] },
  polygon: { color: '#2f81f7', opacity: 0.38, outlineColor: '#79c0ff', outlineWidth: 1.2 }
}

export function ensureFeatureIds(featureCollection) {
  const features = (featureCollection.features || []).map((feature, index) => {
    const id = String(feature.id || (feature.properties && feature.properties.__gwFeatureId) || `feature-${index + 1}`)
    return {
      ...feature,
      id,
      properties: { ...(feature.properties || {}), __gwFeatureId: id }
    }
  })
  return { type: 'FeatureCollection', features }
}

export function inferGeometryType(featureCollection) {
  const types = new Set((featureCollection.features || []).map(feature => feature.geometry && feature.geometry.type).filter(Boolean))
  return types.size === 1 ? [...types][0] : types.size ? 'Mixed' : 'Unknown'
}

export function inferSchema(featureCollection) {
  const fields = new Map()
  ;(featureCollection.features || []).forEach(feature => {
    Object.entries(feature.properties || {}).forEach(([name, value]) => {
      if (name === '__gwFeatureId') return
      const type = value === null || value === undefined ? 'null' : Array.isArray(value) ? 'array' : typeof value
      if (!fields.has(name) || fields.get(name) === 'null') fields.set(name, type)
    })
  })
  return [...fields.entries()].map(([name, type]) => ({ name, type }))
}

export function geometryBounds(featureCollection) {
  const bounds = [Infinity, Infinity, -Infinity, -Infinity]
  const visit = (coordinates) => {
    if (!Array.isArray(coordinates)) return
    if (typeof coordinates[0] === 'number') {
      bounds[0] = Math.min(bounds[0], coordinates[0])
      bounds[1] = Math.min(bounds[1], coordinates[1])
      bounds[2] = Math.max(bounds[2], coordinates[0])
      bounds[3] = Math.max(bounds[3], coordinates[1])
      return
    }
    coordinates.forEach(visit)
  }
  ;(featureCollection.features || []).forEach(feature => feature.geometry && visit(feature.geometry.coordinates))
  return Number.isFinite(bounds[0]) ? bounds : null
}

export function createVectorLayer(dataset, overrides = {}) {
  const data = ensureFeatureIds(dataset.data)
  const bounds = geometryBounds(data)
  return {
    id: uid('layer'),
    name: dataset.name || 'Untitled layer',
    type: 'vector',
    visible: true,
    opacity: 1,
    source: {
      kind: 'local-file',
      format: dataset.format || 'GeoJSON',
      fileName: dataset.fileName || dataset.name,
      fileSize: dataset.fileSize || 0
    },
    crs: dataset.crs || 'EPSG:4326',
    metadata: {
      extent: bounds,
      featureCount: data.features.length,
      geometryType: inferGeometryType(data),
      schema: inferSchema(data),
      ...(dataset.metadata || {})
    },
    style: JSON.parse(JSON.stringify(VECTOR_STYLE)),
    data,
    status: 'ready',
    ...overrides
  }
}

export function createXYZLayer({ name, url, attribution = '' }) {
  return createRemoteTileLayer({ type: 'xyz', name, url, attribution })
}

export function createRemoteTileLayer({ type = 'xyz', name, url, attribution = '', layers = '', format = 'image/png' }) {
  const tileUrl = type === 'wms' ? wmsTileUrl({ url, layers, format }) : url
  return {
    id: uid('layer'),
    name: name || (type === 'wms' ? 'WMS layer' : type === 'wmts' ? 'WMTS layer' : 'XYZ tiles'),
    type,
    visible: true,
    opacity: 1,
    source: { kind: type, url: tileUrl, endpoint: url, attribution, layers, format },
    crs: 'EPSG:3857',
    metadata: { extent: null },
    style: {},
    status: 'ready'
  }
}

export function createRasterLayer(dataset, overrides = {}) {
  return {
    id: uid('layer'),
    name: dataset.name || 'Untitled raster',
    type: 'raster',
    visible: true,
    opacity: 1,
    source: {
      kind: 'local-file',
      format: dataset.format || 'GeoTIFF',
      fileName: dataset.fileName || dataset.name,
      fileSize: dataset.fileSize || 0
    },
    crs: dataset.crs || 'EPSG:4326',
    metadata: {
      extent: dataset.extent || null,
      width: dataset.width,
      height: dataset.height,
      displayWidth: dataset.displayWidth || dataset.width,
      displayHeight: dataset.displayHeight || dataset.height,
      bands: dataset.bands || 1,
      dtype: dataset.dtype || 'Unknown',
      nodata: dataset.nodata ?? null,
      resolution: dataset.resolution || null,
      ...(dataset.metadata || {})
    },
    style: { colorRamp: 'grayscale', band: 1 },
    data: dataset.data,
    status: 'ready',
    ...overrides
  }
}

export function createOpenStreetMapLayer() {
  const provider = BasemapProviders.openStreetMap
  return {
    id: 'basemap-osm',
    name: provider.name,
    type: provider.type,
    visible: true,
    opacity: 1,
    source: {
      kind: provider.type,
      url: provider.url,
      attribution: provider.attribution
    },
    crs: 'EPSG:3857',
    metadata: { provider: 'OpenStreetMap', extent: [-180, -85.0511, 180, 85.0511] },
    style: {},
    status: 'ready',
    system: true
  }
}
